import { useState, useRef } from "react";
import { X, ImagePlus, Film, Loader2, Play } from "lucide-react";
import { api, fileUrl } from "../lib/api";
import { useI18n } from "../i18n";
import { toast } from "sonner";

export const MediaUpload = ({ media = [], onChange, testId = "media-upload" }) => {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const { t } = useI18n();

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of Array.from(files)) {
        const form = new FormData();
        form.append("file", file);
        const res = await api.post("/upload", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push({ path: res.data.path, type: res.data.type });
      }
      onChange([...media, ...uploaded]);
    } catch (e) {
      toast.error(t("error_upload"));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const removeAt = (i) => onChange(media.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-3">
      {media.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {media.map((m, i) => (
            <div
              key={i}
              data-testid={`${testId}-item-${i}`}
              className="relative group aspect-square rounded-md overflow-hidden border border-border bg-muted"
            >
              {m.type === "image" ? (
                <img src={fileUrl(m.path)} alt="upload" className="w-full h-full object-cover" />
              ) : (
                <div className="relative w-full h-full">
                  <video
                    src={fileUrl(m.path)}
                    className="w-full h-full object-cover"
                    muted
                    playsInline
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                    <div className="h-10 w-10 rounded-full bg-white/90 flex items-center justify-center">
                      <Play className="h-4 w-4 text-foreground ml-0.5" fill="currentColor" />
                    </div>
                  </div>
                </div>
              )}
              <button
                type="button"
                onClick={() => removeAt(i)}
                data-testid={`${testId}-remove-${i}`}
                className="absolute top-2 right-2 h-7 w-7 rounded-full bg-white border border-border flex items-center justify-center hover:bg-destructive hover:text-destructive-foreground hover:border-destructive transition-colors"
              >
                <X className="h-3.5 w-3.5" />
              </button>
              <div className="absolute bottom-2 left-2 rounded-full bg-black/70 text-white px-2 py-0.5 text-[10px] tracking-wider uppercase font-semibold flex items-center gap-1">
                {m.type === "image" ? (
                  <ImagePlus className="h-2.5 w-2.5" />
                ) : (
                  <Film className="h-2.5 w-2.5" />
                )}
                {m.type}
              </div>
            </div>
          ))}
        </div>
      )}

      <div
        data-testid={testId}
        onClick={() => !uploading && inputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          handleFiles(e.dataTransfer.files);
        }}
        className="cursor-pointer border-2 border-dashed border-border rounded-md p-6 flex flex-col items-center justify-center gap-2 hover:border-foreground/30 hover:bg-secondary/50 transition-colors"
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*,video/*"
          multiple
          onChange={(e) => handleFiles(e.target.files)}
          className="hidden"
          data-testid={`${testId}-input`}
        />
        {uploading ? (
          <>
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            <span className="text-xs text-muted-foreground">{t("uploading")}</span>
          </>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <div className="h-10 w-10 rounded-full bg-secondary flex items-center justify-center">
                <ImagePlus className="h-5 w-5 text-muted-foreground" strokeWidth={1.8} />
              </div>
              <div className="h-10 w-10 rounded-full bg-secondary flex items-center justify-center">
                <Film className="h-5 w-5 text-muted-foreground" strokeWidth={1.8} />
              </div>
            </div>
            <div className="text-center">
              <div className="text-sm font-medium">{t("media_add")}</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {t("media_hint")}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
